import { useEffect } from 'react';
import { COURSES } from '../home/components/TrainingCoursesBanner';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://rwa99.cn';

export default function CoursesSEOHead() {
  useEffect(() => {
    // --- Page title & meta ---
    document.title =
      'RWA99 课程中心 | 区块链 & RWA 全系列专业课程 · 8门系统化培训课程';

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(
        `meta[${attr}="${name}"]`
      ) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta(
      'description',
      'RWA99课程中心 — 8门系统化区块链与RWA专业培训课程，覆盖区块链原理、ERC标准体系、RWA发行合规、香港SFC监管、碳资产代币化等核心主题，业内专家主讲，永久有效。'
    );
    setMeta(
      'keywords',
      'RWA课程,区块链培训,RWA合规课程,ERC标准培训,香港RWA合规,碳资产RWA,DeFi培训,区块链金融课程,RWA99'
    );
    setMeta('robots', 'index, follow');
    setMeta('og:title', 'RWA99 课程中心 | 区块链 & RWA 全系列专业培训', true);
    setMeta(
      'og:description',
      '8门系统化专业课程，从零基础到RWA合规发行全覆盖，业内专家主讲，视频课持续更新中。',
      true
    );
    setMeta('og:type', 'website', true);
    setMeta('og:url', `${SITE_URL}/courses`, true);
    setMeta('og:site_name', 'RWA99.cn', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', 'RWA99 课程中心 | 区块链 & RWA 全系列专业培训');
    setMeta(
      'twitter:description',
      '8门系统化专业课程，从零基础到RWA合规发行全覆盖，业内专家主讲。'
    );

    // --- Schema.org JSON-LD ---
    const existing = document.querySelectorAll('script[data-courses-jsonld]');
    existing.forEach((el) => el.remove());

    const courseItems = COURSES.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@type': 'Course',
        '@id': `${SITE_URL}/courses/${c.id}`,
        url: `${SITE_URL}/courses/${c.id}`,
        name: c.title,
        description: c.subtitle,
        provider: {
          '@type': 'Organization',
          name: 'RWA99.cn',
          url: SITE_URL,
        },
        educationalLevel: c.level,
        timeRequired: `PT${parseInt(c.duration)}H`,
        offers: {
          '@type': 'Offer',
          price: c.price.replace('¥', ''),
          priceCurrency: 'CNY',
          availability: 'https://schema.org/PreOrder',
        },
        inLanguage: 'zh-CN',
        numberOfCredits: c.lessons,
        hasCourseInstance: {
          '@type': 'CourseInstance',
          courseMode: 'online',
          inLanguage: 'zh-CN',
        },
      },
    }));

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${SITE_URL}/courses#webpage`,
        url: `${SITE_URL}/courses`,
        name: 'RWA99 课程中心 | 区块链 & RWA 全系列专业培训课程',
        description:
          'RWA99课程中心 — 8门系统化区块链与RWA专业培训课程，覆盖区块链原理、ERC标准体系、RWA发行合规、香港SFC监管、碳资产代币化等核心主题。',
        inLanguage: 'zh-CN',
        isPartOf: { '@id': `${SITE_URL}/#website` },
        publisher: { '@id': `${SITE_URL}/#organization` },
        datePublished: '2026-01-01',
        dateModified: '2026-03-30',
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
            {
              '@type': 'ListItem',
              position: 2,
              name: '课程中心',
              item: `${SITE_URL}/courses`,
            },
          ],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        '@id': `${SITE_URL}/courses#courselist`,
        url: `${SITE_URL}/courses`,
        name: 'RWA99 区块链与RWA专业培训课程列表',
        description:
          '8门从区块链基础到RWA合规实操的系统化专业培训课程',
        numberOfItems: COURSES.length,
        itemListElement: courseItems,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'RWA99课程适合哪些人学习？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '适合金融从业者、律师、会计师、企业高管、资产管理人、以及对区块链与RWA感兴趣的投资人。入门课程无需技术背景，专业课程适合有一定金融或区块链基础的学员。',
            },
          },
          {
            '@type': 'Question',
            name: 'RWA99课程多久可以学完？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '单门课程为8-16课时不等，全系列8门共约102课时。购买后永久有效，可按自己节奏学习。视频课程正在录制中，将陆续上线。',
            },
          },
          {
            '@type': 'Question',
            name: '课程支持退款吗？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '支持7天无理由退款。完课后颁发RWA99学习证书，课程含配套课件资料及学员社群答疑。',
            },
          },
        ],
      },
    ];

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-courses-jsonld', 'true');
      script.textContent = JSON.stringify(schema, null, 2);
      document.head.appendChild(script);
    });

    return () => {
      document.querySelectorAll('script[data-courses-jsonld]').forEach((el) =>
        el.remove()
      );
    };
  }, []);

  return null;
}
